import React, { useEffect, useState } from 'react';
import { api, openProposalPdf } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  FileSignature, Download, CheckCircle2, MessageSquare, History, Eye,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';

function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

const STATUS_COLOR = {
  Sent: 'bg-indigo-100 text-indigo-700',
  Accepted: 'bg-emerald-100 text-emerald-700',
  'Changes requested': 'bg-amber-100 text-amber-700',
};

export default function ClientPortalProposalsPage() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);          // currently open proposal object
  const [reqOpen, setReqOpen] = useState(false);
  const [reqComment, setReqComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/portal/proposals');
      setRows(data || []);
    } catch (e) {
      setRows([]);
      toast.error('Could not load proposals');
    }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/portal/proposals/${id}`);
      setOpen(data);
    } catch (e) {
      toast.error('Could not open proposal');
    }
  };

  const accept = async () => {
    if (!open) return;
    setBusy(true);
    try {
      await api.post(`/portal/proposals/${open.id}/accept`, {});
      toast.success('Thanks — the proposal has been accepted.');
      setOpen(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const requestChanges = async () => {
    if (!open) return;
    if (!reqComment.trim()) return toast.error('Please tell us what you would like changed.');
    setBusy(true);
    try {
      await api.post(`/portal/proposals/${open.id}/request-changes`, { comment: reqComment.trim() });
      toast.success('Thanks — we have shared your note with the team.');
      setReqOpen(false); setReqComment(''); setOpen(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="client-portal-proposals-page">
      <div>
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
          <FileSignature className="h-6 w-6 text-green-ink" /> Proposals
        </h1>
        <p className="text-sm text-ink-2 mt-1">Review, accept or request changes on proposals shared with you.</p>
      </div>

      {rows === null ? (
        <Card className="shine-card p-5 rounded-xl space-y-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-16 w-full" />
        </Card>
      ) : rows.length === 0 ? (
        <Card className="shine-card p-10 text-center rounded-xl">
          <FileSignature className="h-10 w-10 text-ink-2 mx-auto mb-3 opacity-50" />
          <h3 className="font-semibold text-ink">No proposals shared yet</h3>
          <p className="text-sm text-ink-2 mt-1">When your team shares a proposal with you, it will appear here.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((r) => (
            <Card key={r.id} className="shine-card p-4 rounded-xl space-y-2" data-testid={`portal-proposal-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-mono font-semibold text-ink">
                    {r.number}
                    {r.revision > 1 && <Badge variant="outline" className="ml-1 text-[10px]">R{r.revision}</Badge>}
                  </div>
                  <div className="text-xs text-ink-2 mt-0.5">
                    {r.subject_line || 'Advertisement Proposal'}
                  </div>
                </div>
                <Badge className={`${STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-700'} border-0`}>
                  {r.status}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-1">
                {(r.services || []).map((s) => (
                  <Badge key={s} variant="outline" className="text-[10px]">{s.replace('_', ' ')}</Badge>
                ))}
              </div>
              <div className="text-sm text-ink flex items-center justify-between pt-2 border-t border-ink/5">
                <span className="text-ink-2 text-xs flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}
                </span>
                <span className="font-bold text-green-ink">{fmtINR(r.total)}</span>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" onClick={() => openDetail(r.id)} data-testid={`portal-proposal-view-${r.id}`}>
                  <Eye className="h-3.5 w-3.5 mr-1" /> View
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => openProposalPdf(r.id, {
                          mode: 'download',
                          filename: `${r.number || 'proposal'}.pdf`,
                          onError: (m) => toast.error(m),
                        })}
                        data-testid={`portal-proposal-pdf-${r.id}`}>
                  <Download className="h-3.5 w-3.5 mr-1" /> PDF
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog — kept intentionally light. No internal fields. */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="portal-proposal-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-ink">
              <FileSignature className="h-5 w-5 text-green-ink" />
              {open?.number}
              {open?.revision > 1 && <Badge variant="outline">R{open.revision}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {open && (
            <div className="space-y-4 text-sm">
              <div className="text-ink-2 text-xs">
                {open.subject_line || 'Advertisement Proposal'}
                {open.sent_at && <> · Sent {new Date(open.sent_at).toLocaleDateString()}</>}
              </div>

              {/* Line items — hide every internal column. */}
              <Card className="shine-card p-3 rounded-xl">
                <h3 className="text-xs font-semibold text-ink-2 uppercase mb-2">Line items</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-ink-2 uppercase border-b border-ink/10">
                      <tr>
                        <th className="py-1.5 pr-2">#</th>
                        <th className="py-1.5 pr-2">Service</th>
                        <th className="py-1.5 pr-2">Details</th>
                        <th className="py-1.5 pr-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(open.lines || []).map((ln, i) => (
                        <tr key={i} className="border-b border-ink/5">
                          <td className="py-1.5 pr-2">{i + 1}</td>
                          <td className="py-1.5 pr-2">
                            <Badge variant="outline" className="text-[10px]">
                              {(ln.service_key || '').replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="py-1.5 pr-2">
                            {ln.service_key === 'theatre_ads' && (
                              <>
                                {ln.attributes?.theatre || ln.attributes?.theatre_code} · {ln.network}{ln.duration_seconds ? ` · ${ln.duration_seconds}s` : ''} · {ln.screens}×{ln.weeks}w
                                {ln.season_summary && <div className="text-[10px] text-ink-2">Seasons: {ln.season_summary}</div>}
                              </>
                            )}
                            {ln.service_key === 'newspaper_eenadu' && `${ln.newspaper} · ${ln.edition} · ${ln.width_cm}×${ln.height_cm}cm · ${ln.colour}`}
                            {(ln.service_key === 'custom' || (ln.service_key || '').startsWith('product_catalog')) && (
                              <>
                                <div>{ln.item || ln.description}</div>
                                {ln.item && ln.description && (
                                  <div className="text-[10px] text-ink-2 whitespace-pre-wrap">{ln.description}</div>
                                )}
                              </>
                            )}
                            {ln.service_key === 'messaging_campaign' && (
                              <>
                                <div>{ln.channel_label || ln.channel_key}</div>
                                <div className="text-[10px] text-ink-2">
                                  {Number(ln.recipients || 0).toLocaleString('en-IN')} {ln.unit_label || 'message'}
                                  {ln.billing_event ? ` · ${ln.billing_event}` : ''}
                                </div>
                                {ln.description && <div className="text-[10px] text-ink-2 whitespace-pre-wrap">{ln.description}</div>}
                              </>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 text-right tabular-nums">{fmtINR(ln.line_total ?? ln.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card className="shine-card p-3 rounded-xl grid grid-cols-2 gap-3 text-xs">
                <div><b>Subtotal:</b> {fmtINR(open.subtotal)}</div>
                {open.discount_pct > 0 && <div><b>Discount:</b> {open.discount_pct}% ({fmtINR(open.discount_amount)})</div>}
                <div><b>GST @ {open.gst_pct}%:</b> {fmtINR(open.gst_amount)}</div>
                <div className="col-span-2 text-lg font-bold text-green-ink pt-1 border-t border-ink/10">
                  Total: {fmtINR(open.total)}
                </div>
              </Card>

              {open.history && open.history.length > 0 && (
                <Card className="shine-card p-3 rounded-xl">
                  <h3 className="text-xs font-semibold text-ink-2 uppercase mb-2 flex items-center gap-1">
                    <History className="h-3 w-3" /> Revision history
                  </h3>
                  <ul className="space-y-1 text-xs text-ink-2 max-h-32 overflow-y-auto">
                    {open.history.filter((h) => /Revision|Sent|Accepted|requested/i.test(h.action)).map((h, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-mono">{new Date(h.at).toLocaleString()}</span>
                        <span className="text-ink">{h.action}</span>
                        {h.note && <span>· "{h.note}"</span>}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="flex items-center justify-between gap-2">
            <Button variant="outline"
                    onClick={() => open && openProposalPdf(open.id, {
                      mode: 'download',
                      filename: `${open.number || 'proposal'}.pdf`,
                      onError: (m) => toast.error(m),
                    })}>
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReqOpen(true)} disabled={busy || !['Generated', 'Sent', 'Follow-up'].includes(open?.status)} data-testid="portal-request-changes-btn">
                <MessageSquare className="h-4 w-4 mr-1" /> Request changes
              </Button>
              <Button onClick={accept} disabled={busy || !['Generated', 'Sent', 'Follow-up'].includes(open?.status)} data-testid="portal-accept-btn">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Accept
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request-changes dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>What would you like changed?</DialogTitle>
          </DialogHeader>
          <Textarea rows={5} value={reqComment} onChange={(e) => setReqComment(e.target.value)}
                    placeholder="e.g. Please add two more theatres in Vizag and share a revised quote."
                    data-testid="portal-request-changes-comment" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReqOpen(false)}>Cancel</Button>
            <Button onClick={requestChanges} disabled={busy || !reqComment.trim()}
                    data-testid="portal-request-changes-submit">
              Send to team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
